import * as React from 'react';
import DashboardPageLayout from 'src/frontend-common/components/DashboardPageLayout';
import QueryValuesLayout from 'src/components/Layouts/QueryValuesLayout';

import { GetListLimit } from 'src/dto/Limits';
import { GetCompaniesSort } from 'src/dto/Company';
import ChartWrapper from 'src/frontend-common/components/Charts/Wrapper';

import AdminDashboardTable from './Table';
import {
  useGetCompanyLookupsQuery,
  useGetCompanyUsageQuery,
  useGetPaymentsQuery,
  useGetPeriodOverviewQuery,
  useGetProductLookupsQuery,
  useGetProductUsageQuery,
} from 'src/redux/services/adminDashboard';
import { TileProps } from 'src/frontend-common/dto/Interfaces/ITile';

import LoadingScreen from 'src/frontend-common/components/LoadingScreen';
import { Card, CardHeader, Grid, Typography, useTheme } from '@mui/material';
import { DateRange, shortTextFormat } from 'src/utils/date';

import DateFilter from 'src/components/DateFilter';
import { format } from 'date-fns';
import TopCountries from './TopCountries';
import { currencyFixedDigitsFormat, dotsFormat } from 'src/utils/number';
import useGetCurrency from 'src/frontend-common/hooks/useGetCurrency';
import LatestSignUps from './LatestSignUps';

interface Props {
  limit: GetListLimit;
  setLimit: (s: GetListLimit) => void;
  page: number;
  setPage: (s: number) => void;
  sort: GetCompaniesSort;
  setSort: (s: GetCompaniesSort) => void;
  dateRange: DateRange;
  setDateRange: (d: DateRange) => void;
}

const AdminDashboard: React.FC<Props> = ({ dateRange, setDateRange }) => {
  const {
    data,
    isLoading,
    isError: paymentsError,
    isFetching: paymentsFetching,
  } = useGetPaymentsQuery({
    start_date: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    end_date: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
  });

  const {
    data: totalRevenue,
    isFetching: isFetchingTotalCost,
    isError: isErrorTotalCost,
  } = useGetPeriodOverviewQuery({
    average_type: 'sum',
    field: 'v3:credits',
    start_date: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    end_date: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
  });

  const {
    data: averageDailyLookups,
    isFetching: isFetchingAverageDailyLookups,
    isError: isErroraverageDailyLookups,
  } = useGetPeriodOverviewQuery({
    average_type: 'moving_average',
    field: 'v3:usage_detail',
    start_date: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    end_date: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
  });

  const {
    data: totalLookups,
    isFetching: isFetchingTotalCalls,
    isError: isErrorTotalLookups,
  } = useGetPeriodOverviewQuery({
    average_type: 'sum',
    field: 'v3:usage_detail',
    start_date: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    end_date: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
  });

  const {
    data: productUsageData,
    isLoading: isLoadingProductUsage,
    isError: isErrorProductUsage,
    isFetching: isFetchingProductUsage,
  } = useGetProductUsageQuery({
    start_date: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    end_date: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
  });

  const {
    data: companyUsageData,
    isLoading: isLoadingCompanyUsage,
    isError: isErrorCompanyUsage,
    isFetching: isFetchingCompanyUsage,
  } = useGetCompanyUsageQuery({
    start_date: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    end_date: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
  });

  const {
    data: productLookupsUsage,
    isLoading: isLoadingProductLookupsUsage,
    isError: isErrorProductLookupsUsage,
    isFetching: isFetchingProductLookupsUsage,
  } = useGetProductLookupsQuery({
    start_date: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    end_date: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    field: 'v3:usage_detail',
    average_type: 'sum',
  });
  const {
    data: productLookupsCredits,
    isLoading: isLoadingProductLookupsCredits,
    isError: isErrorProductLookupsCredits,
    isFetching: isFetchingProductLookupsCredits,
  } = useGetProductLookupsQuery({
    start_date: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    end_date: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    field: 'v3:credits',
    average_type: 'sum',
  });

  const {
    data: companyLookupsUsage,
    isLoading: isLoadingCompanyLookupsUsage,
    isError: isErrorCompanyLookupsUsage,
    isFetching: isFetchingCompanyLookupsUsage,
  } = useGetCompanyLookupsQuery({
    start_date: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    end_date: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    field: 'v3:usage_detail',
    average_type: 'sum',
  });
  const {
    data: companyLookupsCredits,
    isLoading: isLoadingCompanyLookupsCredits,
    isError: isErrorCompanyLookupsCredits,
    isFetching: isFetchingCompanyLookupsCredits,
  } = useGetCompanyLookupsQuery({
    start_date: format(dateRange.startDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    end_date: format(dateRange.endDate, "yyyy-MM-dd'T'HH:mm:ss.SSS") + 'UTC',
    field: 'v3:credits',
    average_type: 'sum',
  });

  const theme = useTheme();
  const Tile: React.FC<TileProps> = React.lazy(() => import('FrontendAnalytics/Tile'));

  const allValuesAreZero = (data: Record<string, Record<string, number>>): boolean => {
    return Object.values(data).every((entry) => Object.values(entry).every((value) => value === 0));
  };

  const dateFormatter = (s: string): string => {
    const date = new Date(s);
    return shortTextFormat(date).toString();
  };

  if (isFetchingTotalCost || isFetchingTotalCalls || isFetchingAverageDailyLookups) return <LoadingScreen variant="circle" />;
  return (
    <DashboardPageLayout>
      <Grid
        container
        justifyContent="space-between"
        style={{
          padding: theme.spacing(3, 0),
        }}
      >
        <Grid item xs={12} md={6}>
          <Typography variant="h2">Admin Dashboard</Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <DateFilter
            dateRange={dateRange}
            setDateRange={(range) => {
              setDateRange(range);
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} style={{ marginBottom: theme.spacing(3) }}>
        <React.Suspense fallback={<LoadingScreen variant="circle" />}>
          <Tile title="Total lookups" isError={isErrorTotalCost} totalData={totalLookups} mdSize={4} />
          <Tile title="Average Daily Lookups" isError={isErroraverageDailyLookups} totalData={averageDailyLookups} mdSize={4} />
          <Tile title="Total revenue" isError={isErrorTotalLookups} totalData={totalRevenue} mdSize={4} currency={true} totNotRounded={true} />
        </React.Suspense>
      </Grid>

      <Grid container spacing={3} style={{ marginBottom: theme.spacing(3) }}>
        <Grid item xs={12} md={6}>
          <ChartWrapper
            key={'company-lookups-chart'}
            isLoading={isLoadingCompanyLookupsUsage || isLoadingCompanyLookupsCredits || isFetchingCompanyLookupsUsage || isFetchingCompanyLookupsCredits}
            isError={isErrorCompanyLookupsCredits || isErrorCompanyLookupsUsage}
            components={[
              {
                chart: {
                  data: companyLookupsUsage
                    ? Object.entries(companyLookupsUsage).map(([key, value]) => ({
                        xAxisLabel: dateFormatter(key),
                        values: value,
                      }))
                    : [],
                  height: 500,
                  stackedBars: true,
                  cartesianGrid: { horizontalLines: true, verticalLines: true },
                  type: 'barChart',
                  applyDefaultSort: false,
                  tooltipValueFormatter: (n) => dotsFormat(n),
                  tooltipFooterValueFormatter: (n) => dotsFormat(n),
                },
                isEmpty:
                  (companyLookupsUsage && allValuesAreZero(companyLookupsUsage)) || (companyLookupsUsage && Object.entries(companyLookupsUsage).length === 0),
                tabLabel: 'Lookups',
                title: 'Company Lookups',
              },
              {
                chart: {
                  data: companyLookupsCredits
                    ? Object.entries(companyLookupsCredits).map(([key, value]) => ({
                        xAxisLabel: dateFormatter(key),
                        values: value,
                      }))
                    : [],
                  height: 500,
                  cartesianGrid: { horizontalLines: true, verticalLines: true },
                  stackedBars: true,
                  type: 'barChart',
                  applyDefaultSort: false,
                  tooltipValueFormatter: (n) =>
                    currencyFixedDigitsFormat(n, 2, {
                      value: useGetCurrency(),
                      currencyPosition: 'left',
                    }),
                  tooltipFooterValueFormatter: (n) =>
                    currencyFixedDigitsFormat(n, 2, {
                      value: useGetCurrency(),
                      currencyPosition: 'left',
                    }),
                },
                isEmpty:
                  (companyLookupsCredits && allValuesAreZero(companyLookupsCredits)) ||
                  (companyLookupsCredits && Object.entries(companyLookupsCredits).length === 0),
                tabLabel: 'Revenue',
                title: 'Company Lookups',
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Company Usage" style={{ paddingLeft: 17 }} />
            <AdminDashboardTable
              tableCells={['Company', 'Lookups', 'Usage', 'Balance']}
              dataType="company"
              data={companyUsageData || []}
              isLoading={isLoadingCompanyUsage || isFetchingCompanyUsage}
              isError={isErrorCompanyUsage}
            />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartWrapper
            key={'product-usage-chart'}
            isLoading={isLoadingProductLookupsUsage || isFetchingProductLookupsUsage || isLoadingProductLookupsCredits || isFetchingProductLookupsCredits}
            isError={isErrorProductLookupsCredits || isErrorProductLookupsUsage}
            components={[
              {
                chart: {
                  cartesianGrid: { horizontalLines: true, verticalLines: true },
                  data: productLookupsUsage
                    ? Object.entries(productLookupsUsage).map(([key, value]) => {
                        const new_value = { ...value };
                        const oldKey = 'bot_detection';
                        const newKey = 'session';
                        const scoreOldKey = 'score';
                        const scoreNewKey = 'onboarding';

                        if (oldKey in new_value) {
                          (new_value as any)[newKey] = new_value[oldKey];
                          delete new_value[oldKey];
                        }
                        if (scoreOldKey in new_value) {
                          (new_value as any)[scoreNewKey] = new_value[scoreOldKey];
                          delete new_value[scoreOldKey];
                        }

                        return {
                          xAxisLabel: dateFormatter(key),
                          values: new_value,
                        };
                      })
                    : [],
                  applyDefaultSort: false,
                  height: 500,
                  stackedBars: true,
                  type: 'barChart',
                  tooltipValueFormatter: (n) => dotsFormat(n),
                  tooltipFooterValueFormatter: (n) => dotsFormat(n),
                },
                isEmpty:
                  (productLookupsUsage && allValuesAreZero(productLookupsUsage)) || (productLookupsUsage && Object.entries(productLookupsUsage).length === 0),
                tabLabel: 'Lookups',
                title: 'Product Lookups',
              },
              {
                chart: {
                  cartesianGrid: { horizontalLines: true, verticalLines: true },
                  data: productLookupsCredits
                    ? Object.entries(productLookupsCredits).map(([key, value]) => {
                        const new_value = { ...value };
                        const oldKey = 'bot_detection';
                        const newKey = 'session';

                        const scoreOldKey = 'score';
                        const scoreNewKey = 'onboarding';

                        if (oldKey in new_value) {
                          (new_value as any)[newKey] = new_value[oldKey];
                          delete new_value[oldKey];
                        }
                        if (scoreOldKey in new_value) {
                          (new_value as any)[scoreNewKey] = new_value[scoreOldKey];
                          delete new_value[scoreOldKey];
                        }
                        return {
                          xAxisLabel: dateFormatter(key),
                          values: new_value,
                        };
                      })
                    : [],
                  applyDefaultSort: false,
                  height: 500,
                  stackedBars: true,
                  type: 'barChart',
                  tooltipValueFormatter: (n) =>
                    currencyFixedDigitsFormat(n, 2, {
                      value: useGetCurrency(),
                      currencyPosition: 'left',
                    }),
                  tooltipFooterValueFormatter: (n) =>
                    currencyFixedDigitsFormat(n, 2, {
                      value: useGetCurrency(),
                      currencyPosition: 'left',
                    }),
                },
                isEmpty:
                  (productLookupsCredits && allValuesAreZero(productLookupsCredits)) ||
                  (productLookupsCredits && Object.entries(productLookupsCredits).length === 0),
                tabLabel: 'Revenue',
                title: 'Product Lookups',
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Product Usage" style={{ paddingLeft: 17 }} />
            <AdminDashboardTable
              tableCells={['Product', 'Lookups', 'Amount']}
              dataType="product"
              data={productUsageData || []}
              isLoading={isLoadingProductUsage || isFetchingProductUsage}
              isError={isErrorProductUsage}
            />
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <LatestSignUps dateRange={{ startDate: dateRange.startDate, endDate: dateRange.endDate }} key="latest-signups" />
        </Grid>
        <Grid item xs={12} md={6}>
          <TopCountries dateRange={{ startDate: dateRange.startDate, endDate: dateRange.endDate }} key="top-countries" />
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Latest Payments" style={{ paddingLeft: 17 }} />
            <AdminDashboardTable
              tableCells={['Company', 'Api Key', 'Amount', 'Date']}
              dataType="payment"
              data={data?.items || []}
              isLoading={isLoading || paymentsFetching}
              isEmpty={data?.items && data?.items.length === 0}
              isError={paymentsError}
            />
          </Card>
        </Grid>
      </Grid>
    </DashboardPageLayout>
  );
};

const WrappedAdminDashboardPage: React.FC = () => (
  <QueryValuesLayout excludeValues={['dateRange', 'token', 'tab']}>
    {({ limit, setLimit, page, setPage, sort, setSort, dateRange, setDateRange }) => (
      <AdminDashboard
        limit={limit}
        setLimit={setLimit}
        page={page}
        setPage={setPage}
        sort={sort}
        setSort={setSort}
        dateRange={dateRange}
        setDateRange={setDateRange}
      />
    )}
  </QueryValuesLayout>
);
export { WrappedAdminDashboardPage as AdminDashboardPage };
